/**
 * LeadSyncFormModal — Finance Partner Sync modal for OneButton dashboard.
 *
 * Lets users:
 * - Sync with an account id (search and link existing finance partner lead).
 * - View existing leads in a card list and select which to sync.
 * - Sync selected leads (resetSyncData) or create new finance partner leads.
 *
 * Types live in LeadSyncFormModal.types.ts; handlers are defined at top level
 * (no functions between DOM) per project coding standards.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { notification } from 'antd';
import { X, CheckCircle, User, ArrowsClockwise, Warning, Table, SquaresFour } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import quoteServices, { type LeadSyncPayload } from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import FinancePartnerCreateLeadProgressModal from './FinancePartnerCreateLeadProgressModal';
import type { LeadSyncFormModalProps, FinancePartnerOption, FinancePartnerLead } from './LeadSyncFormModal.types';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/LeadSyncFormModal.css';
import { OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../../../helpers/contractApiErrorMessage';

export type { LeadSyncFormModalProps } from './LeadSyncFormModal.types';

/** Same partner, different spacing → one dropdown key. */
function normalizePartnerKey(name: string | null | undefined): string {
    return String(name ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

function isIgsOrLightreachPartner(name: string | null | undefined): boolean {
    const k = normalizePartnerKey(name);
    return k === 'igs' || k.includes('lightreach');
}

const MISSING_SALESPERSON_MESSAGE =
    'No Trinity Salesperson has been assigned to this opportunity in Salesforce or the salesperson has not yet synced from Salesforce to oneBUTTON.';

function hasOpportunitySalesperson(opportunityDetails: any): boolean {
    const nested = opportunityDetails?.OpportunityDetails ?? opportunityDetails?.opportunityDetails;
    const salesPerson =
        opportunityDetails?.SalesPerson ??
        opportunityDetails?.salesPerson ??
        nested?.SalesPerson ??
        nested?.salesPerson;

    if (salesPerson) {
        return [
            salesPerson.Name,
            salesPerson.name,
            salesPerson.Email,
            salesPerson.email,
            salesPerson.Phone,
            salesPerson.phone,
            salesPerson.Sales_Division,
            salesPerson.sales_Division,
            salesPerson.Team,
            salesPerson.team,
        ].some((value) => String(value ?? '').trim() !== '');
    }

    return [
        opportunityDetails?.SalespersonName,
        opportunityDetails?.SalesPersonName,
        opportunityDetails?.sfdcTrinitySalesPerson,
        nested?.SalespersonName,
        nested?.SalesPersonName,
        nested?.sfdcTrinitySalesPerson,
    ].some((value) => String(value ?? '').trim() !== '');
}

function shouldBlockLightreachMissingSalesperson(
    partnerName: string | null | undefined,
    opportunityDetails: any,
): boolean {
    return normalizePartnerKey(partnerName).includes('lightreach') && !hasOpportunitySalesperson(opportunityDetails);
}

function bumpFinanceSelectionRefresh(): void {
    useOneButtonStore.getState().setFinanceSelectionRefreshTrigger(Date.now());
}

/**
 * Trinity `sunlight-lead-model.component.ts` ngOnInit — `availableFinancePartners` only includes
 * opportunity products whose Finance_Partner is Sunlight, LightReach, Good Leap, or Enfin (deduped by display name).
 */
function isSunlightLeadModalEligiblePartnerRow(productRow: any): boolean {
    const raw = String(
        productRow?.Finance_Partner ??
            productRow?.finance_Partner ??
            productRow?.Finance_Partner_Display_Name ??
            productRow?.FinancePartnerDisplayName ??
            '',
    ).trim();
    if (!raw) return false;
    const n = normalizePartnerKey(raw);
    return (
        n === normalizePartnerKey(COMMON_CONSTANTS.SunlightPartnerName) ||
        n === normalizePartnerKey(COMMON_CONSTANTS.LightreachPartnerName) ||
        n === normalizePartnerKey(COMMON_CONSTANTS.GoodLeapPartnerName) ||
        n === 'goodleap' ||
        n === normalizePartnerKey('Enfin')
    );
}

// ---------------------------------------------------------------------------
// LeadCard — single lead card (logic at top, no functions between DOM)
// ---------------------------------------------------------------------------

/**
 * Renders one lead as a selectable card: name, date, pills, address, sub-labels, selection icon.
 * All formatting and handlers are defined above the return so there are no functions between DOM.
 */
const LeadCard: React.FC<{
    lead: FinancePartnerLead;
    isSelected: boolean;
    onSelect: (lead: FinancePartnerLead) => void;
}> = ({ lead, isSelected, onSelect }) => {
    // Formatted display values (computed once at top)
    const addressLine1 = lead.Street || '';
    const addressLine2 = [lead.City, lead.State].filter(Boolean).join(', ');
    const formattedDate = lead.LastModifiedDate
        ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MMM D, YYYY')
        : null;

    const handleClick = () => onSelect(lead);
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(lead);
        }
    };

    return (
        <div
            className={`obd-lead-sync-form__card ${isSelected ? 'obd-lead-sync-form__card--selected' : ''} ${lead.IsCurrentLead ? 'obd-lead-sync-form__card--current' : ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            {/* Name (left) and last modified date (right) */}
            <div className="obd-lead-sync-form__card-head">
                <h4 className="obd-lead-sync-form__card-name">
                    {lead.FirstName} {lead.LastName}
                </h4>
                {formattedDate && (
                    <span className="obd-lead-sync-form__card-date">{formattedDate}</span>
                )}
            </div>
            {/* Lead type and finance partner as pills */}
            <div className="obd-lead-sync-form__card-meta">
                <span className="obd-lead-sync-form__card-pill">{lead.lead_Type}</span>
                <span className="obd-lead-sync-form__card-pill">{lead.financePartnerName}</span>
            </div>
            {(addressLine1 || addressLine2) && (
                <p className="obd-lead-sync-form__card-address">
                    {addressLine1}
                    {addressLine1 && addressLine2 && <br />}
                    {addressLine2}
                </p>
            )}
            {/* Extended Details */}
            <div className="obd-lead-sync-form__card-body">
                <div className="obd-lead-sync-form__card-row">
                    <span className="obd-lead-sync-form__card-key">Lead ID</span>
                    <span className="obd-lead-sync-form__card-val">{lead.FinancePartnerSyncLeadId || '—'}</span>
                </div>
                {lead.SalespersonName && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Sales</span>
                        <span className="obd-lead-sync-form__card-val">{lead.SalespersonName}</span>
                    </div>
                )}

                {((lead as any).Status || (lead as any).MatchStatus) && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Match Status</span>
                        <span className="obd-lead-sync-form__card-val">{(lead as any).Status || (lead as any).MatchStatus}</span>
                    </div>
                )}
                <div className="obd-lead-sync-form__card-row" title={lead.TitleVerified ? 'Title verified' : 'Title not verified'}>
                    <span className="obd-lead-sync-form__card-key">Title</span>
                    <span className={`obd-lead-sync-form__card-val ${lead.TitleVerified ? 'obd-lead-sync-form__card-val--ok' : 'obd-lead-sync-form__card-val--err'}`}>
                        {lead.TitleVerified ? <CheckCircle size={16} weight="fill" /> : <X size={16} weight="bold" />}
                    </span>
                </div>
            </div>
            {/* Selection indicator (checkmark or empty circle) */}
            <div className="obd-lead-sync-form__card-select">
                {isSelected ? (
                    <CheckCircle size={22} weight="fill" className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--on" />
                ) : (
                    <div className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--off" aria-hidden />
                )}
            </div>
        </div>
    );
};

export const LeadSyncFormModal: React.FC<LeadSyncFormModalProps> = ({ open, onClose }) => {
    // ----- Store (OneButton global state) -----
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const theme = useOneButtonStore((s) => s.theme);
    const partnerName = useOneButtonStore((s) => s.leadSyncFormPartnerName);
    const openAction = useOneButtonStore((s) => s.leadSyncFormAction);
    const hideCreateLead = useOneButtonStore((s) => s.leadSyncFormHideCreateLead);
    const setRawStatuses = useOneButtonStore((s) => s.setFinancePartnerRawSyncStatuses);

    // ----- Local state -----
    const [viewType, setViewType] = useState<'card' | 'table'>('table');
    const [accountId, setAccountId] = useState('');
    const [financePartnerId, setFinancePartnerId] = useState('');
    const [syncLoading, setSyncLoading] = useState(false);
    const [leads, setLeads] = useState<FinancePartnerLead[]>([]);
    const [selectedLeads, setSelectedLeads] = useState<FinancePartnerLead[]>([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [createProgressOpen, setCreateProgressOpen] = useState(false);
    const [isCreatingInline, setIsCreatingInline] = useState(false);
    const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
    const [needsRefreshOnClose, setNeedsRefreshOnClose] = useState(false);

    const [noticeAPI, noticeContextHolder] = notification.useNotification();

    const financePartners = useMemo((): FinancePartnerOption[] => {
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const byNormDisplay = new Map<string, FinancePartnerOption>();
        products.forEach((p: any) => {
            if (!isSunlightLeadModalEligiblePartnerRow(p)) return;
            const id = String(p.Finance_Partner_Id ?? p.FinancePartnerId ?? '').trim();
            const name = String(p.Finance_Partner_Display_Name ?? p.Finance_Partner ?? p.FinancePartnerDisplayName ?? '').trim();
            if (!id || !name) return;
            const dedupe = normalizePartnerKey(name);
            if (dedupe && !byNormDisplay.has(dedupe)) byNormDisplay.set(dedupe, { value: id, label: name });
        });
        const list = Array.from(byNormDisplay.values());

        if (partnerName) {
            const pn = normalizePartnerKey(partnerName);
            const exists = list.some(
                (opt) =>
                    normalizePartnerKey(opt.label) === pn || normalizePartnerKey(String(opt.value)) === pn,
            );
            if (!exists && pn) {
                list.push({ value: partnerName.toLowerCase(), label: partnerName });
            }
        }

        if (list.length === 0) {
            return [
                { value: 'goodleap', label: COMMON_CONSTANTS.GoodLeapPartnerName },
                { value: 'lightreach', label: COMMON_CONSTANTS.LightreachPartnerName },
                { value: 'sunlight', label: COMMON_CONSTANTS.SunlightPartnerName },
                { value: 'enfin', label: 'Enfin' },
            ];
        }
        return list;
    }, [opportunityDetails?.OpportunityProduct, opportunityDetails?.opportunityProduct, partnerName]);

    /** Products hub Good Leap Application — restrict dropdown to the active sync partner only */
    const financePartnersDropdown = useMemo((): FinancePartnerOption[] => {
        if (!partnerName) return financePartners;
        const pn = normalizePartnerKey(partnerName);
        const filtered = financePartners.filter(
            (opt) =>
                normalizePartnerKey(opt.label) === pn || normalizePartnerKey(String(opt.value)) === pn,
        );
        if (filtered.length > 0) return filtered;
        if (pn.includes('good') && pn.includes('leap')) {
            return [{ value: 'goodleap', label: COMMON_CONSTANTS.GoodLeapPartnerName }];
        }
        return [{ value: partnerName.toLowerCase(), label: partnerName }];
    }, [financePartners, partnerName]);

    const refreshOpportunity = useCallback(async () => {
        if (!opportunityID) return;
        const state = useOneButtonStore.getState();
        const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityID;
        const detail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
        if (detail) state.setOpportunityDetails(detail);
    }, [opportunityID]);

    const fetchLeads = useCallback(async () => {
        if (!opportunityID) return;
        setLeadsLoading(true);
        try {
            const res = await opportunityInfoServices.getFinancePartnerLeads(opportunityID);
            let rawLeads: FinancePartnerLead[] = [];
            if (Array.isArray(res)) rawLeads = res;
            else if (res?.Leads && Array.isArray(res.Leads)) rawLeads = res.Leads;
            else if (res?.data && Array.isArray(res.data)) rawLeads = res.data;

            // Filter leads by the selected partner name for consistency across modals
            if (partnerName) {
                const pName = partnerName.toUpperCase();
                rawLeads = rawLeads.filter(l => (l.financePartnerName || '').toUpperCase() === pName);
            }

            setLeads(rawLeads);
            // Pre-select leads that are already synced (or single lead if only one)
            const oppDetailLocal = useOneButtonStore.getState().opportunityDetails;
            const list = oppDetailLocal?.financePartnerLeadList ?? oppDetailLocal?.FinancePartnerLeadList ?? [];
            const idSet = new Set(
                Array.isArray(list)
                    ? list.map((x: any) => (typeof x === 'string' ? x : x?.Finance_Partner_Sync_LeadID ?? x?.FinancePartnerSyncLeadId)).filter(Boolean)
                    : []
            );
            // Favor the currently active lead, then fallback to the first one in the list
            const activeLead = rawLeads.find((l) => l.IsCurrentLead) || (rawLeads.length > 0 ? rawLeads[0] : null);
            setSelectedLeads(activeLead ? [activeLead] : []);
            if (activeLead) setAccountId(activeLead.FinancePartnerSyncLeadId);
        } catch (err) {
            console.error("Failed to fetch leads", err);
            setLeads([]);
        } finally {
            setLeadsLoading(false);
        }
    }, [opportunityID, partnerName]);

    // ----- Effect: fetch leads when modal opens -----
    useEffect(() => {
        if (open && opportunityID) {
            setAccountId('')
            setMessage(null);
            fetchLeads();
        }
    }, [open, opportunityID, fetchLeads]);

    // ----- Effect: auto-select finance partner based on partnerName or single option (matches Angular when length === 1) -----
    useEffect(() => {
        if (!open) return;

        if (partnerName) {
            const pn = normalizePartnerKey(partnerName);
            const match = financePartnersDropdown.find(
                (p) => normalizePartnerKey(p.label) === pn || normalizePartnerKey(String(p.value)) === pn,
            );
            if (match) setFinancePartnerId(match.value);
        } else if (financePartnersDropdown.length === 1) {
            setFinancePartnerId(financePartnersDropdown[0].value);
        }
    }, [open, financePartnersDropdown, partnerName]);

    // ----- Handlers: sync with account id (search by account id) -----
    const handleSyncWithAccountId = async () => {
        const trimmed = accountId?.trim() ?? '';
        if (!trimmed || !opportunityID) return;
        if (shouldBlockLightreachMissingSalesperson(partnerName, opportunityDetails)) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }
        setSyncLoading(true);
        setMessage(null);
        try {
            const res = await quoteServices.financePartnerLeadSearch(
                String(opportunityID),
                trimmed,
                financePartnerId || ''
            );
            const isSuccess = res?.IsSuccessful === true ||
                res?.IsSuccessful === "true" ||
                res?.IsSuccessful === "True" ||
                (typeof res === 'string' && res.toLowerCase().includes('successfully')) ||
                (res?.Message && res.Message.toLowerCase().includes('successfully'));

            if (isSuccess) {
                // We note that a refresh is needed later when modal closes
                setNeedsRefreshOnClose(true);

                if (isIgsOrLightreachPartner(partnerName)) {
                    bumpFinanceSelectionRefresh();
                }

                // Still fetch leads so table updates visually
                await fetchLeads();
                await quoteServices.contractGetLoanStatusByOpportunity(String(opportunityID) as string | number);

                const successMsg = res?.Message || `Successfully synced and linked with Account ID: ${trimmed}`;
                setMessage({ type: 'success', text: successMsg });
            } else {
                let msg = "Account search request failed.";
                const resultStr = res?.Message || JSON.stringify(res);
                try {
                    const parsed = JSON.parse(resultStr);
                    msg = parsed?.message || parsed?.Message || msg;
                } catch (e) {
                    msg = resultStr || msg;
                }
                setMessage({ type: 'error', text: msg });
            }
        } catch (err: unknown) {
            const errorMsg = getContractApiErrorMessage(
                err,
                'An error occurred in account search request.',
            );
            setMessage({
                type: 'error',
                text: errorMsg,
            });
        } finally {
            setSyncLoading(false);
        }
    };

    /** Toggles lead selection (Single Selection mode). */
    const handleLeadSelect = (lead: FinancePartnerLead) => {
        setSelectedLeads((current) => {
            const isSelected = current.some((l) => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId);
            if (isSelected) {
                // Deselect if clicking the already selected single item
                setAccountId('');
                return [];
            } else {
                // Exclusively select the newly clicked item
                setAccountId(lead.FinancePartnerSyncLeadId);
                return [lead];
            }
        });
    };

    // ----- Handlers: Create Lead inline for Lightreach -----
    const handleCreateLightreachLead = async () => {
        if (!opportunityID) return;
        if (shouldBlockLightreachMissingSalesperson(partnerName, opportunityDetails)) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }
        setIsCreatingInline(true);
        setMessage(null);
        try {
            // Step 1: Reset sync data (Clears current mappings)
            await quoteServices.resetSyncData(String(opportunityID), []);
            
            // Step 2: Create lead with default Solar category as per Angular logic
            const res = await quoteServices.createLead({
                opportunityId: String(opportunityID),
                productSelection: {
                    Product_Category: "S",
                    LeadForProduct: "S"
                }
            });

            if (res === true || res === "true" || res?.IsSuccessful === true) {
                bumpFinanceSelectionRefresh();

                // Refresh raw sync statuses in store so checklist updates
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) setRawStatuses(list);

                // Refresh the cards list and opportunity state
                await refreshOpportunity();
                await fetchLeads();

                // Lightreach getLoanStatus
                await quoteServices.contractGetLoanStatusByOpportunity(String(opportunityID) as string | number);
            } else {
                const msg = getContractApiResultErrorMessage(
                    res,
                    'Some error occurred in Create lead request',
                );
                setMessage({ type: 'error', text: msg });
            }
        } catch (err: unknown) {
            setMessage({
                type: 'error',
                text: getContractApiErrorMessage(err, 'Failed to create lead'),
            });
        } finally {
            setIsCreatingInline(false);
        }
    };

    // ----- Derived: can submit account sync; product list for Create New Lead modal -----
    const canSyncAccount = (accountId?.trim() ?? '').length > 0 && !!opportunityID;
    const productsForCreate = useMemo(() => {
        const all = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const out: { productType: string; category: string }[] = [];
        all.forEach((p: any) => {
            const type = p.ProductCode || p.ProductType || 'Solar';
            const category = p.ProductCategory || p.Product_Category || type;
            if (!out.some((o) => o.productType === type)) out.push({ productType: type, category });
        });
        if (out.length === 0) out.push({ productType: 'Solar', category: 'Solar' });
        return out;
    }, [opportunityDetails]);

    // ----- Handlers: form inputs and modals (no inline functions in JSX) -----
    const handleAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => setAccountId(e.target.value);
    const handleFinancePartnerChange = (value: string) => setFinancePartnerId(value);
    
    const handleOpenCreateProgress = () => {
        if (shouldBlockLightreachMissingSalesperson(partnerName, opportunityDetails)) {
            setMessage({ type: 'error', text: MISSING_SALESPERSON_MESSAGE });
            return;
        }
        if (partnerName?.toUpperCase() === 'LIGHTREACH') {
            handleCreateLightreachLead();
        } else {
            setCreateProgressOpen(true);
        }
    };

    /** Sync currently selected leads with the opportunity (resetSyncData API). */
    const handleSyncSelectedLeads = async () => {
        if (!opportunityID || selectedLeads.length === 0) return;
        setSyncLoading(true);
        setMessage(null);
        try {
            const payload: LeadSyncPayload[] = selectedLeads.map((l) => ({
                finance_Partner_Lead_Sync_Id: l.FinancePartnerSyncLeadId,
                productType: l.lead_Type,
                address: true,
                title: true,
                product_Category: l.lead_Type,
            }));
            const res = await quoteServices.resetSyncData(String(opportunityID), payload);
            if (res?.IsSuccessful === true) {
                if (isIgsOrLightreachPartner(partnerName)) {
                    bumpFinanceSelectionRefresh();
                }

                // Refresh sync statuses
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) setRawStatuses(list);

                setMessage({ type: 'success', text: res?.Message ?? 'Leads synced successfully.' });
                setTimeout(() => onClose(), 1500);
            } else {
                setMessage({ type: 'error', text: res?.Message ?? 'Sync failed.' });
            }
        } catch (err: unknown) {
            setMessage({
                type: 'error',
                text: getContractApiErrorMessage(err, 'An error occurred.'),
            });
        } finally {
            setSyncLoading(false);
        }
    };

    /** Close create-lead modal and refetch leads so the list stays in sync. */
    const handleCreateProgressClose = useCallback(async () => {
        setCreateProgressOpen(false);
        setLeadsLoading(true);
        try {
            await fetchLeads();
            // Refresh the overall sync status so the checklist updates
            if (opportunityID) {
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(list);
            }
        } finally {
            setLeadsLoading(false);
        }
    }, [fetchLeads, opportunityID]);

    const setIsBackgroundSyncing = useOneButtonStore((s) => s.setIsBackgroundSyncing);

    /** Trigger full sync data updates on close if an action was taken */
    const handleCloseModal = async () => {
        onClose(); // Hide instantly so underlying UI shows loading state
        if (needsRefreshOnClose && opportunityID) {
            setIsBackgroundSyncing(true);
            try {
                const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                const list = stats?.FinancePartnerSyncStatuses || stats || [];
                if (Array.isArray(list)) setRawStatuses(list);

                await refreshOpportunity();
            } catch (err) {
                console.error("Failed to refresh on close", err);
            } finally {
                setIsBackgroundSyncing(false);
            }
        }
    };

    // ----- Render -----
    return (
        <>
            <DashboardModal
                open={open}
                onClose={handleCloseModal}
                footer={null}
                title={`Finance Partner Sync ${partnerName ? `— ${partnerName}` : ''}`}
                width="95%"
                maskClosable={false}
                zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
                theme={theme}
                className={`obd-sync-modal obd-lead-sync-form-modal obd-theme-${theme}`}
                rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
                destroyOnClose
                loading={leadsLoading || syncLoading || isCreatingInline}
                loadingMessage={isCreatingInline ? 'Creating Lead...' : syncLoading ? 'Processing...' : 'Loading leads...'}
            >
                {noticeContextHolder}
                <div className="obd-sync-modal__inner obd-lead-sync-form__inner" style={{ position: 'relative', paddingBottom: 20 }}>
                    <div className="obd-lead-sync-form__body">

                        {/* Leads section: scrollable grid or table (if any) */}
                        <div className="obd-lead-sync-form__leads-section">
                            <div className="obd-lead-sync-form__leads-header">
                                    <h4 className="obd-lead-sync-form__leads-title">LEADS</h4>
                                    <div className="obd-quotes-view-toggle">
                                        <button
                                            type="button"
                                            className={`obd-quotes-view-btn ${viewType === 'card' ? 'obd-quotes-view-btn--active' : ''}`}
                                            onClick={() => setViewType('card')}
                                            title="Card View"
                                        >
                                            <SquaresFour size={16} weight="regular" /><span>Card</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`obd-quotes-view-btn ${viewType === 'table' ? 'obd-quotes-view-btn--active' : ''}`}
                                            onClick={() => setViewType('table')}
                                            title="Table View"
                                        >
                                            <Table size={16} weight="regular" /><span>Table</span>
                                        </button>
                                    </div>
                                </div>

                                {leads.length === 0 || viewType === 'table' ? (
                                    <div className="obd-lead-sync-form__table-container">
                                        <table className="obd-lead-sync-form__table">
                                            <thead>
                                                <tr>
                                                    <th className="obd-lead-sync-form__th">Select Lead</th>
                                                    <th className="obd-lead-sync-form__th">Lead ID</th>
                                                    <th className="obd-lead-sync-form__th">Lead Type</th>
                                                    <th className="obd-lead-sync-form__th">Finance Partner</th>
                                                    <th className="obd-lead-sync-form__th">First Name</th>
                                                    <th className="obd-lead-sync-form__th">Last Name</th>
                                                    <th className="obd-lead-sync-form__th">Address</th>
                                                    <th className="obd-lead-sync-form__th">City</th>
                                                    <th className="obd-lead-sync-form__th">State</th>
                                                    <th className="obd-lead-sync-form__th">Sales Person</th>
                                                    <th className="obd-lead-sync-form__th">Title Verified</th>
                                                    <th className="obd-lead-sync-form__th">Days Since Credit Run</th>
                                                    <th className="obd-lead-sync-form__th">Last Modified</th>
                                                    <th className="obd-lead-sync-form__th">Match Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leads.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={14} className="obd-lead-sync-form__empty" style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                                                            <div>Lead not found.</div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                leads.map((lead) => (
                                                    <tr key={lead.FinancePartnerSyncLeadId} className={lead.IsCurrentLead ? 'active-lead' : ''}>
                                                        <td className="obd-lead-sync-form__td">
                                                            <input 
                                                                type="checkbox" 
                                                                className="obd-lead-sync-form__checkbox"
                                                                checked={selectedLeads.some(l => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId)}
                                                                onChange={() => handleLeadSelect(lead)}
                                                            />
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{lead.FinancePartnerSyncLeadId}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.lead_Type}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.financePartnerName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.FirstName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.LastName}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.Street}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.City}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.State}</td>
                                                        <td className="obd-lead-sync-form__td">{lead.SalespersonName}</td>
                                                        <td className="obd-lead-sync-form__td">
                                                            {lead.TitleVerified ? (
                                                                <CheckCircle size={18} weight="fill" className="obd-lead-sync-form__status-ok" />
                                                            ) : (
                                                                <X size={18} weight="bold" className="obd-lead-sync-form__status-err" />
                                                            )}
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{lead.DaysSinceCreditRun ?? '0'}</td>
                                                        <td className="obd-lead-sync-form__td">
                                                            {lead.LastModifiedDate ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MM-DD-YYYY') : '-'}
                                                        </td>
                                                        <td className="obd-lead-sync-form__td">{(lead as any).Status || (lead as any).MatchStatus || '-'}</td>
                                                    </tr>
                                                ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="obd-lead-sync-form__leads-grid">
                                        {leads.map((lead) => (
                                            <LeadCard
                                                key={lead.FinancePartnerSyncLeadId}
                                                lead={lead}
                                                isSelected={selectedLeads.some(
                                                    (l) => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId
                                                )}
                                                onSelect={handleLeadSelect}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>                        
                    </div>

                    {/* Footer: message (if any) + Sync existing lead / Create New Lead / Close */}
                    <div className="obd-sync-modal__footer obd-lead-sync-form__footer">
                        {message && message.type === 'error' && (
                            <div
                                className="obd-lead-sync-form__message obd-lead-sync-form__message--error"
                                style={{ flexBasis: '100%', order: 3, marginRight: 'auto', marginBottom: 0 }}
                            >
                                <Warning size={16} weight="fill" style={{ marginRight: 6 }} />
                                {message.text}
                            </div>
                        )}
                        {message && message.type === 'success' && (
                            <div
                                className="obd-lead-sync-form__message obd-lead-sync-form__message--success"
                                style={{ flexBasis: '100%', order: 3, marginRight: 'auto', marginBottom: 0 }}
                            >
                                <CheckCircle size={16} weight="fill" style={{ marginRight: 6 }} />
                                {message.text}
                            </div>
                        )}
                        {/* Sync with account id: input + finance partner dropdown + sync button (hidden for IGS) */}
                        {partnerName?.toUpperCase() !== 'IGS' && (
                        <div className="obd-lead-sync-form__sync-row" style={{ marginBottom: 0 }}>
                            <input
                                type="text"
                                className="obd-lead-sync-form__input ant-input"
                                placeholder="Account id"
                                value={accountId}
                                onChange={handleAccountIdChange}
                                aria-label="Account id"
                            />
                            <OBDSelect
                                className="obd-lead-sync-form__select obd-lead-sync-form__select--partner ant-input"
                                value={financePartnerId || undefined}
                                onChange={handleFinancePartnerChange}
                                aria-label="Finance Partner"
                                placeholder="Choose Finance Partner"
                                options={financePartnersDropdown.map((p) => ({
                                    value: p.value,
                                    label: p.label,
                                }))}
                            />
                            <button
                                type="button"
                                className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__sync-btn"
                                onClick={handleSyncWithAccountId}
                                disabled={!canSyncAccount}
                                aria-label="Sync with Account id"
                            >
                                <ArrowsClockwise size={18} weight="bold" />
                                Sync with Account id
                            </button>
                        </div>
                        )}
                        <div className="obd-lead-sync-form__footer-btns">
                            {!hideCreateLead && (
                                <button
                                    type="button"
                                    className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__create-new-btn"
                                    onClick={handleOpenCreateProgress}
                                    disabled={syncLoading || isCreatingInline || !opportunityID}
                                    aria-label="Create a new lead"
                                >
                                    <User size={18} weight="duotone" aria-hidden />
                                    Create a new lead
                                </button>
                            )}
                            <button type="button" className="obd-sync-modal__btn obd-sync-modal__btn--secondary" onClick={handleCloseModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </DashboardModal>

            {/* Create New Lead progress modal (opens from footer button) */}
            {createProgressOpen && opportunityID && (
                <FinancePartnerCreateLeadProgressModal
                    open={createProgressOpen}
                    onClose={handleCreateProgressClose}
                    opportunityId={opportunityID}
                    partnerName={partnerName ?? ''}
                    products={productsForCreate}
                />
            )}
        </>
    );
};

export default LeadSyncFormModal;
